import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useProperty, useIncrementPropertyViews } from '@/hooks/useProperties';
import { PropertyInquiryForm } from '@/components/PropertyInquiryForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Bed,
  Bath,
  Maximize,
  MapPin,
  Calendar,
  Building2,
  Share2,
  ArrowLeft,
  Check,
  Phone,
  Mail,
} from 'lucide-react';
import { useEffect } from 'react';

const PropertyDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: property, isLoading } = useProperty(slug || '');
  const incrementViews = useIncrementPropertyViews();

  useEffect(() => {
    if (property?.id) {
      incrementViews.mutate(property.id);
    }
  }, [property?.id]);

  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-96 w-full mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              <Skeleton className="h-12 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-32 w-full" />
            </div>
            <div>
              <Skeleton className="h-96 w-full" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!property) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <Building2 className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-3xl font-bold mb-4">Property Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The property you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/real-estate">
            <Button>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Properties
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const formatPrice = (price: number | null) => {
    if (!price) return 'Contact for price';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: property.currency || 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getTransactionLabel = () => {
    if (property.transaction_type === 'both') return 'For Sale & Rent';
    if (property.transaction_type === 'sale') return 'For Sale';
    return 'For Rent';
  };

  const getStatusColor = () => {
    switch (property.status) {
      case 'available': return 'bg-green-500';
      case 'reserved': return 'bg-yellow-500';
      case 'sold': return 'bg-red-500';
      case 'rented': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Link to="/real-estate" className="inline-flex items-center text-primary hover:underline mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Properties
        </Link>

        {/* Image Gallery */}
        <div className="mb-8">
          <div className="relative h-[500px] rounded-lg overflow-hidden">
            <img
              src={property.main_image_url || '/placeholder.svg'}
              alt={property.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              <Badge className="bg-primary/90 text-white">
                {getTransactionLabel()}
              </Badge>
              {property.is_featured && (
                <Badge className="bg-accent/90 text-white">Featured</Badge>
              )}
              <Badge className={`${getStatusColor()} text-white`}>
                {property.status.charAt(0).toUpperCase() + property.status.slice(1)}
              </Badge>
            </div>
            <Button
              variant="secondary"
              size="icon"
              className="absolute top-4 right-4"
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: property.title,
                    text: property.short_description || property.title,
                    url: window.location.href,
                  });
                }
              }}
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Gallery Thumbnails */}
          {property.gallery_images_urls && property.gallery_images_urls.length > 0 && (
            <div className="grid grid-cols-4 gap-4 mt-4">
              {property.gallery_images_urls.slice(0, 4).map((image, index) => (
                <div key={index} className="relative h-24 rounded-lg overflow-hidden">
                  <img
                    src={image}
                    alt={`${property.title} - Image ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-110 transition-transform cursor-pointer"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-4xl font-bold mb-3">{property.title}</h1>
              <div className="flex items-center text-muted-foreground mb-4">
                <MapPin className="h-5 w-5 mr-2" />
                <span className="text-lg">
                  {property.location_zone && `${property.location_zone}, `}
                  {property.location_city}
                  {property.location_province && `, ${property.location_province}`}
                </span>
              </div>
              <div className="text-3xl font-bold text-primary mb-2">
                {formatPrice(
                  property.transaction_type === 'rent' ? property.rental_price : property.price
                )}
                {property.transaction_type === 'rent' && (
                  <span className="text-lg text-muted-foreground">/month</span>
                )}
              </div>
              {property.transaction_type === 'both' && property.rental_price && (
                <p className="text-lg text-muted-foreground">
                  Rent: {formatPrice(property.rental_price)}/month
                </p>
              )}
            </div>

            {/* Key Features */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 p-6 bg-muted/30 rounded-lg">
              {property.bedrooms !== null && (
                <div className="text-center">
                  <Bed className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <p className="text-2xl font-bold">{property.bedrooms}</p>
                  <p className="text-sm text-muted-foreground">Bedrooms</p>
                </div>
              )}
              {property.bathrooms !== null && (
                <div className="text-center">
                  <Bath className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <p className="text-2xl font-bold">{property.bathrooms}</p>
                  <p className="text-sm text-muted-foreground">Bathrooms</p>
                </div>
              )}
              {property.area_sqm !== null && (
                <div className="text-center">
                  <Maximize className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <p className="text-2xl font-bold">{property.area_sqm}</p>
                  <p className="text-sm text-muted-foreground">m²</p>
                </div>
              )}
              {property.year_built && (
                <div className="text-center">
                  <Calendar className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <p className="text-2xl font-bold">{property.year_built}</p>
                  <p className="text-sm text-muted-foreground">Built</p>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4">About This Property</h2>
              <div className="text-muted-foreground leading-relaxed space-y-4 break-words overflow-wrap-anywhere">
                {(property.description || property.short_description)?.split('\n').map((paragraph, index) => (
                  paragraph.trim() && (
                    <p key={index} className="mb-3 text-base leading-7 whitespace-pre-wrap">
                      {paragraph}
                    </p>
                  )
                ))}
              </div>
            </div>

            {/* Features */}
            {property.features && property.features.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4">Features</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {property.features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <Check className="h-5 w-5 text-primary mr-2" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Amenities */}
            {property.amenities && property.amenities.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4">Amenities</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {property.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center">
                      <Check className="h-5 w-5 text-primary mr-2" />
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Location */}
            {property.address && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4">Location</h2>
                <p className="text-muted-foreground mb-4">{property.address}</p>
                {property.latitude && property.longitude && (
                  <div className="h-96 rounded-lg overflow-hidden bg-muted">
                    <iframe
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      src={`https://www.google.com/maps?q=${property.latitude},${property.longitude}&z=15&output=embed`}
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-6">
              {/* Inquiry Form */}
              <PropertyInquiryForm propertyId={property.id} propertyTitle={property.title} />

              {/* Contact Card */}
              <div className="p-6 bg-gradient-to-br from-primary/10 to-transparent border border-primary/20 rounded-lg">
                <h3 className="text-xl font-bold mb-4">Contact MARTEVAC</h3>
                <div className="space-y-3">
                  <a
                    href="tel:+18095527353"
                    className="flex items-center text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Phone className="h-5 w-5 mr-3" />
                    +1 (809) 552-7353
                  </a>
                  <a
                    href="mailto:info@martevac.com"
                    className="flex items-center text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Mail className="h-5 w-5 mr-3" />
                    info@martevac.com
                  </a>
                </div>
                <Button className="w-full mt-6">Schedule a Viewing</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PropertyDetail;
